import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { catchError, combineLatest, finalize, map, Observable, of } from 'rxjs';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import DepartmentGroupService from 'src/app/shared/services/department-group.service';
import { DepartmentService } from 'src/app/shared/services/department.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { whitespace } from 'src/app/shared/validators/whitespace.validator';
import { CreateDepartmentRequest, CreateDepartmentDialogData, UpdateDepartmentRequest, Department } from '../../department.model';
import { DepartmentGroup } from '../../departmentGroup.model';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import { PagedResult } from 'src/app/shared/models/paging';
import { OrganizationType } from 'src/app/shared/enums/organization-type.enum';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';
import { AdmG4BusinessAccountNumberComponent } from 'src/app/shared/components/adm-g4-business-account-number/adm-g4-business-account-number.component';
import {
  AddressChanged,
  DepAddressTypeaheadComponent,
  InitialAddress,
} from 'src/app/shared/components/dep-address-typeahead/dep-address-typeahead.component';
import { Guid } from 'guid-typescript';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

export interface CreateEditDepartmentForm {
  departmentName: FormControl<string>;
  departmentGroup: FormControl;
  invoicingAccountNumber: FormControl<number | null>;
  darId: FormControl<Guid | null>;
  departmentAddress: FormControl<string | null>;
  isHeadquarter: FormControl<boolean>;
  agreementNumber: FormControl<number | null>;
  addressWithoutDARValidation: FormControl<boolean>;
}

@Component({
  selector: 'adm-create-update-department-dialog',
  templateUrl: './create-update-department-dialog.component.html',
  styleUrls: ['./create-update-department-dialog.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatOptionModule,
    MatSlideToggleModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatTooltipModule,
    CustomDropDownArrowDirective,
    AdmG4AgreementNumberComponent,
    AdmG4BusinessAccountNumberComponent,
    DepAddressTypeaheadComponent,
  ],
})
export class CreateDepartmentDialogComponent implements OnInit {
  data = inject<CreateDepartmentDialogData>(MAT_DIALOG_DATA);

  private dialogRef = inject<MatDialogRef<CreateDepartmentDialogComponent>>(MatDialogRef);
  private loadingService = inject(TopLoaderService);
  private snackbarService = inject(SnackbarService);
  private departmentService = inject(DepartmentService);
  private departmentGroupService = inject(DepartmentGroupService);

  public dialogForm: FormGroup<CreateEditDepartmentForm> = new FormGroup<CreateEditDepartmentForm>({
    departmentName: new FormControl<string>('', {
      validators: [Validators.required, whitespace(), Validators.maxLength(30)],
      nonNullable: true,
    }),
    departmentGroup: new FormControl(null),
    invoicingAccountNumber: new FormControl(null),
    departmentAddress: new FormControl('', Validators.required),
    darId: new FormControl(null),
    isHeadquarter: new FormControl<boolean>(false, { nonNullable: true }),
    agreementNumber: new FormControl(null, {
      validators: [Validators.pattern('[0-9]*')],
    }),
    addressWithoutDARValidation: new FormControl<boolean>(false, { nonNullable: true }),
  });
  public submitBtnValue = 'Tilføj';
  public isEdit = false;
  public organizationType = OrganizationType;
  public departmentGroups$: Observable<PagedResult<DepartmentGroup>>;
  public g4Info = 'G4 Kontonr til for afdelingen.';
  public okAftaleNr =
    'Det aftalenr., der er oprettet til lokationen i G4 og som også bruges i projekt id. Aftalenummeret anvendes til evt. forbrugsopkrævning, strømgodtgørelse og evt. elafgiftrefusion.';

  filteredAddresses$: Observable<any[]>;

  public async ngOnInit(): Promise<void> {
    this.departmentGroups$ = combineLatest([
      this.departmentGroupService.getDepartmentGroups(this.data.organizationId),
      this.data.departmentId ? this.departmentService.getDepartment(this.data.departmentId) : of(null),
    ]).pipe(
      map(([groups, department]) => {
        if (department) {
          this.setupFormForUpdate(department);
        }
        this.initialAddress.set({
          darId: department?.darId,
          fullAddress: department?.address,
        });
        return groups;
      })
    );
  }

  initialAddress = signal<InitialAddress | null>(null);

  private setupFormForUpdate(department: Department) {
    this.submitBtnValue = 'Gem';
    this.isEdit = true;
    this.dialogForm.controls.departmentName.setValue(department.name);
    this.dialogForm.controls.invoicingAccountNumber.setValue(department.invoicingAccountNumber ?? null);
    this.dialogForm.controls.agreementNumber.setValue(department.agreementNumber ?? null);
    this.dialogForm.controls.departmentGroup.setValue(department.departmentGroupId);
    this.dialogForm.controls.isHeadquarter.setValue(department.isHeadquarter);
    this.dialogForm.controls.departmentAddress.setValue(department.address);
    this.dialogForm.controls.darId.setValue(department.darId);
    if (!department.darId) {
      this.dialogForm.controls.addressWithoutDARValidation.setValue(true);
    }
  }

  public get isLoading(): boolean {
    return this.loadingService.loading;
  }

  public selectAddress(address: AddressChanged | null) {
    if (!address) {
      return;
    }

    if (address.formatAddressToString) {
      this.dialogForm.controls.departmentAddress.setValue(address.formatAddressToString);
    } else {
      this.dialogForm.controls.departmentAddress.setValue(address.fullAddress ?? null);
    }
    this.dialogForm.controls.darId.setValue(address.darId ?? null);
  }

  public save() {
    this.loadingService.setLoading(true);

    if (this.dialogForm.valid) {
      if (this.data.departmentId) {
        this.updateDepartment();
      } else {
        this.createDepartment();
      }
    }
  }
  private updateDepartment() {
    if (!this.data.departmentId) {
      this.loadingService.setLoading(false);
      return;
    }

    const darValidationDisabled = this.dialogForm.controls.addressWithoutDARValidation.value;

    const updateDepartmentModel: UpdateDepartmentRequest = {
      name: this.dialogForm.controls.departmentName.value,
      invoicingAccount: this.dialogForm.controls.invoicingAccountNumber.value,
      agreementNumber: this.dialogForm.controls.agreementNumber.value,
      address: this.dialogForm.controls.departmentAddress.value,
      darId: darValidationDisabled
        ? null
        : this.dialogForm.controls.darId.value == null
          ? null
          : this.dialogForm.controls.darId.value.toString(),
      departmentGroupId: this.dialogForm.controls.departmentGroup.value,
      isHeadquarter: this.dialogForm.controls.isHeadquarter.value,
    };
    this.departmentService
      .updateDepartment(this.data.departmentId, updateDepartmentModel)
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          if (httpError.status == 400 && httpError.error) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 100170) {
              this.snackbarService.warning(`Der findes allerede en afdeling med samme navn.`);
            }
          } else {
            // TODO: log error when able to
            this.snackbarService.error('Der skete en fejl under opdatering af afdelingen.');
            this.dialogRef.close();
          }
          return of();
        }),
        finalize(() => this.loadingService.setLoading(false))
      )
      .subscribe(() => {
        this.snackbarService.success(`Ændringer til afdelingen blev gemt.`);
        this.dialogRef.close({
          departmentId: this.data.departmentId,
          organizationId: this.data.organizationId,
        });
      });
  }

  private createDepartment() {
    const darValidationDisabled = this.dialogForm.controls.addressWithoutDARValidation.value;
    const invoicingAccount = this.dialogForm.controls.invoicingAccountNumber.value;

    const departmentObject: CreateDepartmentRequest = {
      name: this.dialogForm.controls.departmentName.value,
      invoicingAccount: this.dialogForm.controls.invoicingAccountNumber.value ?? null,
      agreementNumber: this.dialogForm.controls.agreementNumber.value,
      address: this.dialogForm.controls.departmentAddress.value,
      darId: darValidationDisabled
        ? null
        : this.dialogForm.controls.darId.value == null
          ? null
          : this.dialogForm.controls.darId.value.toString(),
      departmentGroupId: this.dialogForm.controls.departmentGroup.value,
      isHeadquarter: this.dialogForm.controls.isHeadquarter.value,
    };
    this.departmentService
      .createDepartment(this.data.organizationId, departmentObject)
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          if (httpError.status == 400 && httpError.error) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 100170) {
              this.snackbarService.warning(`Der findes allerede en afdeling med samme navn.`);
            }
          } else {
            // TODO: log error when able to
            this.snackbarService.error('Der skete en fejl under oprettelse af afdelingen.');
            this.dialogRef.close();
          }
          return of();
        }),
        finalize(() => this.loadingService.setLoading(false))
      )
      .subscribe(departmentId => {
        if (departmentId) {
          this.snackbarService.success(`Afdelingen blev oprettet.`);
          this.dialogRef.close({
            departmentId: departmentId,
            organizationId: this.data.organizationId,
          });
        }
      });
  }

  public getControlErrorMessage(fieldIdentifier: string, error: string) {
    let fieldName = 'Navn';
    switch (fieldIdentifier) {
      case 'departmentName':
        fieldName = `Afdelingsnavn`;
        break;
      case 'invoicingAccountNumber':
        fieldName = `G4 Kontonr.`;
        break;
      case 'agreementNumber':
        fieldName = `G4 Aftalenr.`;
        break;
      case 'addressLookup':
        fieldName = `Adresse`;
        break;
    }

    switch (error) {
      case 'number':
        return `${fieldName} skal være et heltal.`;
      case 'minLength':
        return `${fieldName} skal være på mindst 2 karakterer.`;
      case 'maxLength':
        return `${fieldName} må maks være 30 karakterer.`;
      case 'required':
        return `${fieldName} er påkrævet.`;
      case 'empty':
        return `${fieldName} må ikke være tom.`;
    }
  }
}
